import { useAuth } from "@clerk/clerk-react";
import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useAppAuth } from "@/lib/auth";

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * Route wrapper that requires authentication.
 * Shows loading spinner while checking auth state,
 * redirects to sign-in if unauthenticated.
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isLoaded, isSignedIn, signOut } = useAuth();
  const { role, isLoading: isRoleLoading } = useAppAuth();
  const location = useLocation();

  // 1. Wait for Clerk to load
  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-lavender flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 rounded-full border-4 border-primary border-t-transparent animate-spin" />
          <p className="text-sm text-muted-foreground">Loading authentication...</p>
        </div>
      </div>
    );
  }

  // 2. If not signed in, redirect immediately
  if (!isSignedIn) {
    // Save the current location to redirect back after sign-in
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  // 3. Wait for role to load (only if signed in)
  if (isRoleLoading || !role) {
    return (
      <div className="min-h-screen bg-lavender flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 rounded-full border-4 border-primary border-t-transparent animate-spin" />
          <p className="text-sm text-muted-foreground">Loading permissions...</p>
        </div>
      </div>
    );
  }

  // 4. Admin Check
  if (role !== "admin" && role !== "super_admin") {
      return (
        <div className="flex h-screen flex-col items-center justify-center gap-4">
          <h1 className="text-2xl font-bold">Unauthorized</h1>
          <p className="text-muted-foreground">You do not have permission to access the admin dashboard.</p>
          <p className="text-sm text-muted-foreground">Current Role: {role || "None"}</p>
          <button 
            onClick={() => signOut({ redirectUrl: '/sign-in' })} 
            className="rounded bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90 cursor-pointer"
          >
            Switch Account
          </button>
        </div>
      );
  }

  return <>{children}</>;
}
